import { useCallback, useEffect, useMemo, useState } from 'react';

import { PeerTypes, useListCampaignPeers } from '@givebacks/api/store';
import { useDonorTranslations } from 'core';
import { Combobox, Loader, Pill, PillsInput, ScrollArea, useCombobox } from 'ui';
import { useDebouncedValue } from 'ui/Hooks';

import { UsePeerCreditForm } from '../usePeerCredits';
import { NoSearchResult } from './NoSearchResult';
import { CreditValue } from './PeerSelect.types';

const useFilteredSelectOptions = (
  selectData: CreditValue[],
  search: string,
  selectedValues: CreditValue[]
) => {
  return useMemo(() => {
    const filteredOptions = selectData.filter((item) => {
      const matchesSearch = item.label.trim().toLowerCase().includes(search.trim().toLowerCase());
      const isAlreadySelected = selectedValues.some(
        (selectedItem) => selectedItem.value === item.value
      );
      return matchesSearch && !isAlreadySelected;
    });

    return {
      filteredOptions,
      hasMatchingOptions: filteredOptions.length > 0,
    };
  }, [selectData, search, selectedValues]);
};

const SelectOptions = ({ selectData }: { selectData: CreditValue[] }) => {
  return selectData.map((item) => (
    <Combobox.Option value={item.value} key={item.value}>
      {item.label}
    </Combobox.Option>
  ));
};

const PeerMultiSelect = ({
  type,
  form,
  storeId,
  storeItemId,
}: {
  type: PeerTypes;
  form: UsePeerCreditForm;
  storeItemId: string;
  storeId: string;
}) => {
  const { t } = useDonorTranslations({
    storeItemId,
    storeId,
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const [value, setValue] = useState<CreditValue[]>([]);
  const [search, setSearch] = useState('');

  const handleValueSelect = useCallback((val: CreditValue) => {
    setValue((current) => {
      if (current.includes(val)) {
        return [...current];
      }
      return [...current, val];
    });
    setSearch('');
  }, []);

  const handleValueRemove = (value: CreditValue) => {
    setValue((current) => current.filter((credit) => credit.value !== value.value));
    setSearch('');
  };

  const [debouncedSearch] = useDebouncedValue(search, 200, { leading: true });

  const { data: participants, isFetched: isParticipantsFetched } = useListCampaignPeers({
    campaignId: storeItemId,
    causeId: storeId,
    peer_type: 'participants',
    queryOptions: {
      searches: {
        name: { value: debouncedSearch },
      },
      order: {
        field: 'first_name',
        direction: 'asc',
      },
    },
  });

  const { data: teams, isFetched: isTeamsFetched } = useListCampaignPeers({
    campaignId: storeItemId,
    causeId: storeId,
    peer_type: 'teams',
    queryOptions: {
      searches: {
        name: { value: debouncedSearch },
      },
      order: {
        field: 'first_name',
        direction: 'asc',
      },
    },
  });

  const participantSelectData = useMemo(() => {
    return participants?.participants.map((participant) => {
      const fullName = `${participant?.first_name} ${participant?.last_name}`;
      const displayName = participant?.team?.name
        ? `${fullName} - ${participant.team.name}`
        : fullName;
      return {
        value: participant.uuid,
        label: displayName,
        type: 'credit_to',
      };
    });
  }, [participants]);

  const teamSelectData = useMemo(() => {
    return teams?.teams.map((team) => ({
      value: team.uuid,
      label: `${team?.name} `,
      type: 'credit_to',
    }));
  }, [teams]);

  const selectData = useMemo(() => {
    return type === 'participants' ? participantSelectData : teamSelectData;
  }, [participantSelectData, teamSelectData, type]);

  const pills = value.map((item) => (
    <Pill
      key={item.value}
      withRemoveButton
      onRemove={() => handleValueRemove(item)}
      variant="light"
      type={item.type === 'credit_write_in' ? 'error' : 'primary'}
    >
      {item.label}
    </Pill>
  ));

  const isLoading = !isParticipantsFetched || !isTeamsFetched || search !== debouncedSearch;

  const handleSelectionAndClearSearch = useCallback(
    (selectedValue: CreditValue) => {
      handleValueSelect(selectedValue);
      setSearch('');
      combobox.resetSelectedOption();
    },
    [handleValueSelect, setSearch, combobox]
  );

  const placeholder = !value.length
    ? `Search for ${
        type === 'participants' ? t('participants').toLowerCase() : t('teams').toLowerCase()
      }`
    : undefined;

  const { filteredOptions, hasMatchingOptions } = useFilteredSelectOptions(
    selectData ?? [],
    search,
    value
  );

  const handlePillChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearch = event.currentTarget.value;
      setSearch(newSearch);

      // Open dropdown when there's a search query
      if (newSearch.trim()) {
        combobox.openDropdown();
      } else {
        combobox.closeDropdown();
      }

      combobox.updateSelectedOptionIndex();
    },
    [combobox]
  );

  useEffect(() => {
    const credits = value.map((val) => ({ value: val.value, type: val.type, label: val.label }));
    form.setFieldValue('credits', credits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(value: string) => {
        const selectedValue = selectData?.find((item) => item.value === value);
        if (selectedValue) {
          handleSelectionAndClearSearch(selectedValue);
        }
      }}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          rightSection={isLoading ? <Loader size="1rem" /> : null}
          onClick={() => combobox.toggleDropdown()}
        >
          <Pill.Group w="100%">
            {pills}

            <Combobox.EventsTarget>
              <PillsInput.Field
                onBlur={() => combobox.closeDropdown()}
                value={search}
                placeholder={placeholder}
                onChange={handlePillChange}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && search.length === 0 && value.length > 0) {
                    event.preventDefault();
                    handleValueRemove(value[value.length - 1]);
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>
      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize mah="300px">
            {
              // If search is empty or there is a matching option, show the options
              hasMatchingOptions || !search.trim() ? (
                <SelectOptions selectData={filteredOptions} />
              ) : (
                <NoSearchResult
                  isLoading={isLoading}
                  type={type}
                  search={search}
                  onChange={handleSelectionAndClearSearch}
                  closeDropdown={combobox.closeDropdown}
                />
              )
            }
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export { PeerMultiSelect };
